import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Table, Modal, Dropdown, Menu, Select, DatePicker, Spin } from "antd";
import html2pdf from "html2pdf.js/dist/html2pdf.min.js";
import { useCorporateTransactions } from "../../hooks/products/corporate/useCorporateTransactions";
import TransactionStatement from "../../components/TransactionStatement";
import { notifyUser } from '../../util/util';
import { scrollTriggerIntoView } from '../../util/modalUtils';
import LinkSuccessModal from "../dashboard/LinkSuccessModal";
// --- START: CustomPagination Component Definition (Required for UI) ---
function CustomPagination({ current, pageSize, total, onChange }) {
  const pageCount = Math.ceil(total / pageSize);
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const navBtnStyle = {
    background: 'transparent',
    color: '#184b18',
    border: 'none',
    borderRadius: 0,
    padding: '0 10px',
    minWidth: 0,
    height: 26,
    fontWeight: 700,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s',
    boxShadow: 'none',
    margin: '0 2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
  };
  const navBtnDisabledStyle = {
    ...navBtnStyle,
    color: '#bdbdbd',
    cursor: 'not-allowed',
    opacity: 0.6,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        onClick={() => current > 1 && onChange(current - 1)}
        disabled={current === 1}
        style={current === 1 ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Previous page"
      >
        {'<'}
      </button>
      {pages.map(page => (
        <button
          key={page}
          onClick={() => onChange(page)}
          style={{
            border: '0.5px solid #184b18',
            background: page === current ? 'linear-gradient(90deg, #05b005d7 0%, #00b050 60%, #90ee90 100%)' : 'transparent',
            color: page === current ? '#fff' : '#184b18',
            borderRadius: 18,
            padding: '0 14px',
            minWidth: 36,
            height: 36,
            fontWeight: 700,
            fontSize: 12,
            outline: 'none',
            cursor: 'pointer',
            transition: 'background 0.2s, color 0.2s',
            boxShadow: 'none',
            margin: '0 2px',
          }}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => current < pageCount && onChange(current + 1)}
        disabled={current === pageCount}
        style={current === pageCount ? navBtnDisabledStyle : navBtnStyle}
        aria-label="Next page"
      >
        {'>'}
      </button>
    </div>
  );
}
// --- END: CustomPagination Component Definition ---


export default function CorporateTransactionsHistory({ transactions: propTransactions = [], pagination: propPagination = {} }) {
  const location = useLocation();
  const policyNumber = location.state?.productDetails?.policyNumber;
  const { data: transactionsData, isLoading: transactionsLoading } = useCorporateTransactions(policyNumber);
  const transactions = transactionsData || propTransactions;
  const pagination = propPagination; // No pagination in this API
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = pagination.page_size || 10;
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showDownloadSuccessModal, setShowDownloadSuccessModal] = useState(false);
  const [isStatementGenerating, setIsStatementGenerating] = useState(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [statementYear, setStatementYear] = useState([]);
  const [statementError, setStatementError] = useState("");
  const statementPdfRef = useRef();
  const searchInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Notify success when data is loaded
  React.useEffect(() => {
    if (transactions && transactions.length > 0) {
      notifyUser("success", "Corporate transactions loaded successfully", "Success");
    } else if (transactions && transactions.length === 0) {
      notifyUser("error", "No corporate account found or selected", "Error");
    }
  }, [transactions]);

  // Ensure modals open near the trigger on mobile
  React.useEffect(() => { if (showStatementModal || showDownloadSuccessModal) scrollTriggerIntoView(); }, [showStatementModal, showDownloadSuccessModal]);

  // Normalize transactions for table
  const normalizedTransactions = transactions.map(item => ({
    date: item.transaction_date || "",
    transaction: item.payment_flag || item.transaction_type || "",
    reference: item.receipt_number || item.transaction_id || "",
    amount: item.amount || 0,
    company_name: item.company_name || "",
    policy_no: item.policy_no || "",
    narration: item.description || item.requisition_no || ""
  }));

  // Sort by date descending
  const sortedTransactions = [...normalizedTransactions].sort((a, b) => new Date(b.date) - new Date(a.date));

  // Statement filter logic (omitted for brevity)
  const statementFilteredTransactions = React.useMemo(() => {
    if (!statementYear || statementYear.length === 0) return [];
    if (statementYear.includes("all")) return transactions;
    const yearsSet = new Set(statementYear.map(y => Number(y)));
    return transactions.filter(t => {
      if (!t || !t.transaction_date) return false;
      const d = new Date(t.transaction_date);
      if (Number.isNaN(d.getTime())) return false;
      return yearsSet.has(d.getFullYear());
    });
  }, [transactions, statementYear]);

  // Prepare rows and columns for statement (omitted for brevity)
  const statementRows = statementFilteredTransactions.map(item => ({
    date: item.transaction_date || "",
    type: item.payment_flag || item.transaction_type || "",
    reference: item.receipt_number || item.transaction_id || "",
    amount: item.amount || 0,
    policy_no: item.policy_no || ""
  }));

  const statementColumns = [
    {
      title: "DATE",
      dataIndex: "date",
      key: "date",
      widthStyle: { width: '15%', borderRight: '1px solid #888', textAlign: 'center' },
      render: (date) => date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
    },
    {
      title: "TRANSACTION TYPE",
      dataIndex: "type",
      key: "type",
      widthStyle: { width: '20%', borderRight: '1px solid #888', textAlign: 'center' }
    },
    {
      title: "REFERENCE",
      dataIndex: "reference",
      key: "reference",
      widthStyle: { width: '25%', borderRight: '1px solid #888', textAlign: 'center' }
    },
    {
      title: "POLICY NO",
      dataIndex: "policy_no",
      key: "policy_no",
      widthStyle: { width: '20%', borderRight: '1px solid #888', textAlign: 'center' }
    },
    {
      title: "AMOUNT",
      dataIndex: "amount",
      key: "amount",
      widthStyle: { width: '20%', borderRight: 'none', textAlign: 'center' },
      render: (amount) => `GHS ${Math.abs(Number(amount)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
    }
  ];

  // Table columns - Enhanced readability for mobile via `whiteSpace: 'nowrap'`
  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
      // Mobile-friendly date format and alignment
      render: date => <div style={{ textAlign: "left", paddingLeft: 8, whiteSpace: 'nowrap' }}>{formatDate(date)}</div>
    },
    {
      title: "Transaction Type",
      dataIndex: "transaction",
      key: "transaction",
      align: "left"
    },
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      align: "left",
      responsive: ['sm'] // Keep visible on small screen and up
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "center",
      render: amount => <span style={{ color: "#111", fontWeight: 600, whiteSpace: 'nowrap' }}>GHS {Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      align: "left",
      responsive: ['md'] // Hide on small screens to reduce horizontal scroll
    },
    {
      title: "Policy No",
      dataIndex: "policy_no",
      key: "policy_no",
      align: "left",
      responsive: ['sm'] // Keep visible on small screen and up
    },
  ];

  // Filtering logic (omitted for brevity)
  let filteredTransactions = sortedTransactions;
  const isDateFilterActive = !!dateFilter;
  const isSearchActive = !!searchValue;
  if (isDateFilterActive && !isSearchActive) {
    let start, end;
    const today = new Date();
    if (dateFilter === "week") {
      end = today;
      start = new Date();
      start.setDate(today.getDate() - 6);
    } else if (dateFilter === "month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today;
    } else if (dateFilter === "year") {
      start = new Date(today.getFullYear(), 0, 1);
      end = today;
    } else if (dateFilter === "custom" && Array.isArray(customDateRange) && customDateRange[0] && customDateRange[1]) {
      start = customDateRange[0].toDate ? customDateRange[0].toDate() : customDateRange[0];
      end = customDateRange[1].toDate ? customDateRange[1].toDate() : customDateRange[1];
    }
    if (start && end) {
      filteredTransactions = filteredTransactions.filter(t => {
        const d = new Date(t.date);
        return d >= start && d <= end;
      });
    }
  } else if (isSearchActive) {
    const val = searchValue.trim().toLowerCase();
    filteredTransactions = filteredTransactions.filter(t =>
      t.reference.toLowerCase().includes(val) ||
      t.amount.toString().includes(val) ||
      t.policy_no.toLowerCase().includes(val)
    );
  }

  // Pagination (omitted for brevity)
  const paginatedTransactions = React.useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(startIdx, startIdx + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  // Helpers
  function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    // Use an abbreviated format for mobile
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  }
  function getLast5Years() {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let i = 0; i < 5; i++) years.push(currentYear - i);
    return years;
  }

  // Statement modal logic (omitted for brevity)
  function handleStatementDownload() {
    if (!statementYear || statementYear.length === 0) {
      setStatementError("Please select at least one year or All Years");
      return;
    }
    setStatementError("");
    setModalOpen(false);
    setIsStatementGenerating(true);
    setTimeout(() => {
      setShowStatementModal(true);
      setIsStatementGenerating(false);
    }, 300);
  }
  function handlePdfDownload() {
    if (statementPdfRef.current) {
      const node = statementPdfRef.current.querySelector('.pdf-page') || statementPdfRef.current;
      const prevWidth = node.style.width;
      (async () => {
        try {
          setIsPdfDownloading(true);
          // Force table/summary visible for PDF generation on mobile
          node.classList.add('force-pdf-visible');
          node.style.width = '210mm';
          await new Promise(r => setTimeout(r, 60));
          const imgs = node.querySelectorAll('img');
          await Promise.all(Array.from(imgs).map(img => (img.complete ? Promise.resolve() : new Promise(resolve => { img.onload = img.onerror = resolve; }))));

          await html2pdf()
            .set({
              margin: 10,
              filename: `Transaction_Statement.pdf`,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: { scale: 2, useCORS: true, logging: false },
              jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
              pagebreak: { mode: ['css', 'legacy'] }
            })
            .from(node)
            .save();

          setShowDownloadSuccessModal(true);
        } catch (err) {
          console.error('Failed to generate PDF', err);
        } finally {
          node.classList.remove('force-pdf-visible');
          node.style.width = prevWidth;
          setIsPdfDownloading(false);
        }
      })();
    }
  }
  function handleSearchClick() {
    setShowSearch(prev => !prev);
    setTimeout(() => {
      if (!showSearch && searchInputRef.current) searchInputRef.current.focus();
    }, 200);
  }
  function handleDateMenuClick({ key }) {
    if (isSearchActive) return;
    setDateFilter(key);
    if (key === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
      setCustomDateRange([null, null]);
    }
  }

  // Date filter menu (omitted for brevity)
  const dateMenu = (
    <Menu onClick={handleDateMenuClick} selectedKeys={[dateFilter]} style={{ minWidth: 160 }}>
      <Menu.Item key="week" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Week</Menu.Item>
      <Menu.Item key="month" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Month</Menu.Item>
      <Menu.Item key="year" style={{ fontWeight: 500 }} disabled={isSearchActive}>This Year</Menu.Item>
      <Menu.Item key="custom" style={{ fontWeight: 500 }} disabled={isSearchActive}>Custom</Menu.Item>
    </Menu>
  );

  // UI
  return (
    <>
      {/* Success Modal */}
    <LinkSuccessModal
             visible={showDownloadSuccessModal}
             onClose={() => setShowDownloadSuccessModal(false)}
             title="Download Successful"
             subTitle="Your transaction statement PDF has been downloaded."
             closeText="OK"
           />
      <div className="life-page-container" style={{ padding: isMobile ? 8 : 16, width: '100%', boxSizing: 'border-box' }}>
        <div className="life-page-card" style={{ background: '#fff', borderRadius: isMobile ? 16 : 28, boxShadow: '0 8px 40px 0 rgba(0,0,0,0.10)', padding: isMobile ? '16px 12px' : '32px 40px', minWidth: isMobile ? 280 : 320, maxWidth: 900, width: '100%', margin: '0 auto' }}>
     
        <div className="py-4 d-flex flex-column align-items-center">
          
          {/* HEADER/FILTERS ROW - Modified for better mobile stacking */}
          <div className="mb-3 w-100 d-flex flex-wrap align-items-center justify-content-between header-controls-row">
            <div className="mb-2 mb-sm-0">
              <div className="page-title">Corporate Transactions</div>
            </div>
            
            <div className="controls d-flex flex-wrap align-items-center justify-content-end controls-container">
              
              {/* Controls Left - Search and Date Filter */}
              <div className="controls-left d-flex align-items-center controls-left-container" style={{ marginRight: 16 }}>
                
                {/* Search Input and Icon */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by amount, reference, policy no..."
                    className={`form-control border-0 search-field ${showSearch ? 'visible' : ''}`}
                    style={{ background: 'transparent', border: 'none', boxShadow: 'none', textAlign: 'left' }}
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    onBlur={() => { if (!searchValue) setShowSearch(false); }}
                    disabled={isDateFilterActive}
                  />
                  <i
                    className="bi bi-search"
                    style={{ fontSize: 20, cursor: 'pointer', zIndex: 2, padding: 8, borderRadius: '50%', transition: 'background 0.2s', position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: showSearch ? '#f7f7fa' : 'transparent' }}
                    aria-label="Search"
                    onClick={handleSearchClick}
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') handleSearchClick(); }}
                    role="button"
                  ></i>
                </div>
                
                {/* Date Filter Dropdown */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', background: '#fff', borderRadius: 32, boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)', padding: '4px 8px', minHeight: 44 }}>
                    <Dropdown overlay={dateMenu} trigger={["click"]} placement="bottomRight">
                      <button className="btn btn-light border-0 d-flex align-items-center justify-content-center ms-1 date-filter-btn" style={{ borderRadius: 24, height: 36, background: 'transparent', fontWeight: 500, display: 'flex', alignItems: 'center', minWidth: 90, padding: '0 12px' }} title="Filter by date" disabled={isSearchActive}>
                        <span className="d-none d-sm-inline" style={{ whiteSpace: 'nowrap', marginRight: 8 }}>Date</span>
                        <i className="bi bi-calendar" style={{ fontSize: 20 }}></i>
                      </button>
                    </Dropdown>
                  </div>
                </div>

              </div> {/* End controls-left */}

              {/* Controls Right - Generate Statement */}
              <div className="controls-right d-flex align-items-center controls-right-container">
                <button className="btn btn-primary generate-btn" onClick={() => setModalOpen(true)}>Generate Statement</button>
              </div>
            </div>
          </div>
          
          {/* Custom Date Range Picker - Adjusted its positioning for better flow */}
          {showCustomDatePicker && (
            <div className="w-100 px-3 pb-3 custom-date-picker-row corporate-custom-date-row">
              <div className="d-flex justify-content-end" style={{ flexWrap: 'wrap' }}>
                <DatePicker.RangePicker
                  value={Array.isArray(customDateRange) ? customDateRange : [null, null]}
                  onChange={vals => {
                    if (!vals || !vals[0] || !vals[1]) {
                      setCustomDateRange([null, null]);
                      setDateFilter("");
                      setShowCustomDatePicker(false);
                    } else {
                      setCustomDateRange(vals);
                      setDateFilter('custom');
                    }
                  }}
                  allowClear
                  style={{ minWidth: 220, marginBottom: 0, maxWidth: '100%' }}
                  disabled={isSearchActive}
                  format="YYYY-MM-DD"
                />
              </div>
            </div>
          )}
              
          {/* Modals (Logic copied from original code, placement moved down for cleaner flow) */}
          <Modal
            open={modalOpen}
            onCancel={() => { setModalOpen(false); setStatementYear(''); setStatementError(''); }}
            footer={null}
            centered
            closable={false}
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span className="fw-bold">Generate Statement</span>
                <button
                  onClick={() => { setModalOpen(false); setStatementYear(''); setStatementError(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 26,
                    color: '#888',
                    cursor: 'pointer',
                    position: 'absolute',
                    top: 16,
                    right: 24,
                    zIndex: 10
                  }}
                  aria-label="Close"
                >×</button>
              </div>
            }
            bodyStyle={{ padding: '18px 24px 18px 24px', minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}
          >
            <div style={{ marginBottom: 16 }}>
              <p style={{ marginBottom: 16, fontSize: 13 }}>Select the consecutive years you want to generate a statement for</p>
              <div className="mb-3">
                <div className="mt-3">
                  <label className="form-label fw-bold" style={{ display: 'block', marginBottom: 10, fontSize: 15 }}>Select Year(s)</label>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select Year(s)"
                    value={statementYear}
                    onChange={setStatementYear}
                    style={{ width: '100%', minWidth: 220, marginBottom: 12 }}
                    optionLabelProp="label"
                  >
                    <Select.Option value="all" label="All Years">All Years</Select.Option>
                    {getLast5Years().map(year => (
                      <Select.Option key={year} value={year} label={year}>{year}</Select.Option>
                    ))}
                  </Select>
                </div>
                {statementError && <div className="text-danger mt-2">{statementError}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <button className="btn btn-primary mb-4 w-80 mobile-full-width" disabled={isStatementGenerating} onClick={handleStatementDownload}>{isStatementGenerating ? <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}><Spin size="small" /> Generating...</span> : 'Preview & Download PDF'}</button>
            </div>
          </Modal>

          <Modal
            open={showStatementModal}
            onCancel={() => setShowStatementModal(false)}
            footer={null}
            centered
            width={'min(1100px, calc(100vw - 32px))'}
            style={{ maxWidth: '100%' }}
            zIndex={4000}
            title={<div className="fw-bold">Transaction Statement Preview</div>}
            bodyStyle={{ padding: 0, background: '#ffffffff', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}
          >
            <div style={{ padding: 24, background: '#ffffffff' }}>
              {/* Desktop: Full preview */}
              <div className="desktop-preview" style={{ position: 'relative' }}>
                <div ref={statementPdfRef}>
                  <TransactionStatement rows={statementRows} columns={statementColumns} />
                </div>
                {isPdfDownloading && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 40 }}>
                    <div style={{ textAlign: 'center' }}><Spin size="large" /><div style={{ marginTop: 8 }}>Preparing PDF...</div></div>
                  </div>
                )}
              </div>
              {/* Mobile: Simple view with file icon and download button */}
              <div className="mobile-preview-simple" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: '32px 20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  <i className="bi bi-file-earmark-pdf" style={{ fontSize: 48, color: '#dc3545', marginBottom: 12 }}></i>
                  <p style={{ margin: '0 0 20px', color: '#666', fontSize: 14, textAlign: 'center', lineHeight: 1.4 }}>Transaction Statement<br/>PDF</p>
                  <button 
                    className="btn btn-primary" 
                    disabled={isPdfDownloading} 
                    onClick={handlePdfDownload}
                    style={{ width: '100%', maxWidth: 240 }}
                  >
                  {isPdfDownloading ? (
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                      <Spin size="small" /> Downloading...
                    </span>
                  ) : 'Download As PDF'}
                </button>
                </div>
              </div>
              <div className="desktop-download-btn" style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
                <button className="btn btn-primary mb-4 w-80 mobile-full-width" disabled={isPdfDownloading} onClick={handlePdfDownload}>{isPdfDownloading ? <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}><Spin size="small" /> Downloading...</span> : 'Download As PDF'}</button>
              </div>
            </div>
          </Modal>

          {/* Component-level responsive styles - ENHANCED */}
          <style>{`
            /* Mobile/Desktop preview toggle */
            .mobile-preview-simple { display: none !important; }
            @media (max-width: 768px) {
              .desktop-preview { display: none !important; }
              .desktop-download-btn { display: none !important; }
              .mobile-preview-simple { display: block !important; }
            }
            @media (min-width: 769px) {
              .mobile-preview-simple { display: none !important; }
            }
            .transactions-page .page-title { font-weight:700; color:#111; font-size: clamp(18px, 2.2vw, 22px); }
            .transactions-page .search-field { border-radius:24px; padding: 8px 12px; height:36px; width:0; opacity:0; min-width:0; transition: width .28s ease, opacity .18s ease; position:relative; right: -40px; }
            .transactions-page .search-field.visible { width: 260px; min-width:120px; opacity:1; right: 40px; }
            .transactions-page .generate-btn { border-radius:24px; height:36px; font-weight:600; min-width:120px; }
            .transactions-page .card { width: 97%; max-width: 1330px; min-width: 0; }
            /* Table responsive tweaks */
            .transactions-page .ant-table-wrapper { overflow-x: auto; }
            .transactions-page .ant-table-body { overflow-x: auto !important; }
            .transactions-page .ant-table tbody td { font-size: clamp(12px, 1.8vw, 14px); }
            .transactions-page .ant-table thead th { font-size: clamp(12px, 1.8vw, 13px); }
            .transactions-page .mobile-full-width { width: 80%; max-width: 420px; }
            .transactions-page .header-controls-row { padding-left: 12px; padding-right: 12px; }
            
            /* Tablet/Mid-Screen Optimization */
            @media (max-width: 900px) {
              .transactions-page .generate-btn { min-width:96px; padding: 8px 12px; }
              .transactions-page .search-field.visible { width: 200px; }
              .transactions-page .date-filter-btn { min-width: 36px; padding: 0 8px !important; }
            }

            /* Mobile-First Optimization (Small Screens) */
            @media (max-width: 576px) {
              .transactions-page { padding: 8px; }
              .transactions-page .page-title { font-size: 16px; margin-bottom: 12px; }
              
              /* Header/Filters Stacking */
              .transactions-page .header-controls-row { 
                flex-direction: column; 
                align-items: stretch;
                padding-left: 8px; 
                padding-right: 8px; 
                gap: 12px;
              }
              .transactions-page .controls-container { 
                width: 100%; 
                flex-direction: column;
                align-items: stretch;
                gap: 10px;
              }
              .transactions-page .controls-left-container { 
                width: 100%;
                justify-content: flex-start;
                gap: 12px;
                position: relative;
              }
              .transactions-page .controls-right-container { 
                width: 100%;
              }
              .transactions-page .controls-right-container .generate-btn {
                width: 100%;
              }

              /* Search field - full width row on mobile when open */
              .transactions-page .search-field.visible { 
                width: calc(100vw - 100px); 
                min-width: 180px; 
                max-width: none;
                padding: 10px 40px 10px 16px; 
                right: 0;
                position: relative;
                background: '#f7f7fa';
              }
              
              /* Search icon positioning */
              .transactions-page .bi-search {
                position: relative !important;
                right: auto !important;
                transform: none !important;
                top: auto !important;
              }

              /* Date Filter button: Show icon only, fixed position */
              .transactions-page .date-filter-btn { 
                min-width: 44px !important; 
                width: 44px !important;
                height: 44px !important;
                padding: 0 !important; 
                background: #f7f7fa !important;
                box-shadow: 0 2px 8px 0 rgba(0,0,0,0.08);
                flex-shrink: 0;
                border-radius: 50% !important;
              }
              .transactions-page .date-filter-btn span { display: none !important; }

              /* Generate Statement Button */
              .transactions-page .generate-btn { 
                min-width: unset; 
                padding: 10px 16px; 
                font-size: 13px; 
                height: 40px;
              }

              /* Table and Card */
              .transactions-page .card { width: 100%; min-width: 0; border-radius: 10px; }
              .transactions-page .ant-table { font-size: 11px; }
              .transactions-page .ant-table table { min-width: max-content; }
              .transactions-page .ant-table thead th,
              .transactions-page .ant-table tbody td {
                padding: 8px 4px;
                font-size: 11px;
              }
              
              /* Modal Button */
              .transactions-page .mobile-full-width { width: 100% !important; max-width: 100%; }

              /* Custom Date Picker row */
              .transactions-page .custom-date-picker-row .ant-picker { width: 100% !important; }
              .transactions-page .custom-date-picker-row > div { justify-content: flex-start !important; }
              .transactions-page .custom-date-picker-row { padding-left: 8px; padding-right: 8px; }

              /* Pagination */
              .transactions-page .d-flex.justify-content-between.align-items-center.px-4.py-3 {
                flex-direction: column;
                gap: 12px;
                padding: 12px;
              }
              .transactions-page .d-flex.justify-content-between.align-items-center.px-4.py-3 > .text-muted {
                  display: none !important;
              }
            }
            
            /* Extra Small Screens */
            @media (max-width: 480px) {
              .transactions-page .page-title { font-size: 15px; }
              .transactions-page .search-field.visible { 
                width: calc(100vw - 80px); 
                min-width: 150px; 
                padding: 8px 36px 8px 12px;
              }
              .transactions-page .date-filter-btn {
                width: 40px !important;
                height: 40px !important;
                min-width: 40px !important;
              }
              .transactions-page .ant-table { font-size: 10px; }
              .transactions-page .ant-table thead th,
              .transactions-page .ant-table tbody td {
                padding: 6px 2px;
                font-size: 10px;
              }
            }
          `}</style>

          {/* Table Container */}
          <div className="w-100 d-flex justify-content-center corporate-table-container" style={{ minHeight: 120, background: 'transparent' }}>
            <div className="card p-0 shadow-sm corporate-table-card" style={{ width: '97%', maxWidth: 1330, minWidth: 0, color: '#111', overflowX: 'auto', borderRadius: 10, position: 'relative' }}>
              {transactionsLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 20 }}>
                  <Spin size="large" />
                </div>
              )}
              <Table
                columns={columns}
                dataSource={paginatedTransactions}
                rowKey={(record, idx) => idx}
                pagination={false}
                locale={{ emptyText: <span className="text-muted" style={{ fontSize: 18 }}>No transactions found.</span> }}
                style={{ overflow: 'hidden', borderRadius: 24 }}
                scroll={{ x: 'max-content' }} 
              />
            </div>
          </div>
          
          {/* Pagination */}
          <div className="w-100 d-flex justify-content-center mt-2 corporate-pagination-container">
            <div className="d-flex justify-content-between align-items-center px-4 py-3 corporate-pagination-row" style={{ maxWidth: 1330, minWidth: 0, width: '100%', borderRadius: 10, border: 'none', background: 'transparent' }}>
              <div className="text-muted d-none d-sm-block" style={{ fontSize: 15, fontWeight: 500 }}>
                Showing 1-{paginatedTransactions.length} of {filteredTransactions.length} results
              </div>
              <CustomPagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredTransactions.length}
                onChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
        </div>
        {/* Responsive styles for life-page */}
        <style>{`
          @media (max-width: 768px) {
            .life-page-card {
              padding: 32px 24px !important;
            }
            .life-page-container {
              padding: 12px !important;
            }
          }
          @media (max-width: 520px) {
            .life-page-card {
              padding: 24px 16px !important;
            }
            .life-page-container {
              padding: 8px !important;
            }
          }
          @media (max-width: 600px) {
            .life-page-container {
              padding: 4px !important;
              padding-top: 8px !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
            .life-page-card {
              padding: 12px 8px !important;
              border-radius: 12px !important;
              max-width: none !important;
              min-width: auto !important;
              width: 100% !important;
              box-sizing: border-box !important;
            }
          }
        `}</style>
      </div>
    </>
  );
}
